/**
 * Exportación a Markdown (SDD §7.4) y "copiar para IA": contexto, notas de la
 * plantilla, participantes y transcripción completa. Incluye la línea de
 * consentimiento declarado.
 */
import type { RecordingDetail, Summary, TemplateId, TranscriptSegment } from '@eco/shared'
import { formatDate, formatDuration } from './format'
import { noteSections, taskOwner } from './notes'
import { labelToName, segmentAt } from './transcript'

export interface MarkdownOptions {
  /** Versión de notas a incluir (por defecto la última válida). */
  summary?: Summary | null
  transcript?: boolean
  now?: Date
}

/** Última versión utilizable de las notas (editada u ok), si la hay. */
export function latestSummary(summaries: Summary[], templateId?: TemplateId): Summary | null {
  const ok = summaries
    .filter(
      (s) =>
        (s.estado === 'ok' || s.estado === 'editada') &&
        (!templateId || s.templateId === templateId)
    )
    .sort((a, b) =>
      a.creadoEn < b.creadoEn ? 1 : a.creadoEn > b.creadoEn ? -1 : b.version - a.version
    )
  return ok[0] ?? null
}

export function notesMarkdown(
  templateId: TemplateId,
  contenido: unknown,
  nameOf?: (etiqueta: string) => string
): string[] {
  const lines: string[] = []
  for (const s of noteSections(templateId, contenido, nameOf)) {
    lines.push(`## ${s.title}`, '')
    if (s.kind === 'text') lines.push(s.text || '_(vacío)_')
    if (s.kind === 'flag') lines.push(s.label)
    if (s.kind === 'list')
      for (const it of s.items)
        lines.push(
          `- ${it.texto}${it.evidencia[0] ? ` _(${formatDuration(it.evidencia[0].inicioMs)})_` : ''}`
        )
    if (s.kind === 'tasks')
      for (const t of s.items) {
        const owner = taskOwner(t, nameOf)
        const extra = [owner, t.fecha].filter(Boolean).join(', ')
        lines.push(`- [ ] ${t.descripcion}${extra ? ` — _${extra}_` : ''}`)
      }
    lines.push('')
  }
  return lines
}

export function transcriptMarkdown(segments: TranscriptSegment[]): string[] {
  const lines: string[] = []
  let last = ''
  for (const s of segments) {
    if (s.speaker !== last) {
      lines.push('', `**${s.speaker}** · ${formatDuration(s.inicioMs)}`)
      last = s.speaker
    }
    lines.push(s.texto)
  }
  return lines
}

export function buildMarkdown(detail: RecordingDetail, opts: MarkdownOptions = {}): string {
  const { recording, project, segments } = detail
  const summary = opts.summary === undefined ? latestSummary(detail.summaries) : opts.summary
  const lines: string[] = [`# ${recording.titulo}`, '']
  const meta = [`**Fecha:** ${formatDate(recording.fechaInicio)}`]
  if (recording.duracionMs) meta.push(`**Duración:** ${formatDuration(recording.duracionMs)}`)
  if (project) meta.push(`**Proyecto:** ${project.nombre}`)
  if (recording.origen === 'import') meta.push('**Origen:** audio importado')
  lines.push(meta.join(' · '), '')
  if (recording.descripcion?.trim()) lines.push('## Contexto', '', recording.descripcion.trim(), '')

  if (summary)
    lines.push(
      ...notesMarkdown(summary.templateId, summary.contenido, (e) =>
        labelToName(detail.speakers, e)
      )
    )

  if (recording.marcas?.length) {
    lines.push('## Momentos marcados', '')
    for (const ms of recording.marcas) {
      const seg = segments[segmentAt(segments, ms)]
      lines.push(`- ${formatDuration(ms)}${seg ? ` — **${seg.speaker}:** ${seg.texto}` : ''}`)
    }
    lines.push('')
  }

  const hablantes = [...new Set(segments.map((s) => s.speaker))]
  if (hablantes.length) lines.push(`**Participantes:** ${hablantes.join(', ')}`, '')

  if (opts.transcript !== false && segments.length) {
    lines.push('## Transcripción', ...transcriptMarkdown(segments), '')
  }

  const fecha = formatDate((opts.now ?? new Date()).toISOString())
  lines.push(
    '---',
    `_Grabado con eco el ${formatDate(recording.fechaInicio)}. ` +
      `El organizador declaró haber avisado a los participantes: ${recording.consentimientoDeclaradoEn ? 'sí' : 'no'}. ` +
      `Exportado el ${fecha}._`
  )
  return lines.join('\n').replace(/\n{3,}/g, '\n\n') + '\n'
}

/** Nombre de archivo seguro: `2026-09-23 Comité de selección.md`. */
export function exportFilename(titulo: string, fechaIso: string, ext: string): string {
  const fecha = fechaIso.slice(0, 10)
  const base = titulo
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
  return `${fecha} ${base || 'Reunión'}.${ext}`
}

export function downloadText(filename: string, text: string, type = 'text/markdown'): void {
  const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
